/**
 * Open the Recal OAuth flow in a popup window and resolve when the popup
 * posts back its result. Replaces the previous full-page redirect which
 * would wipe unsaved form state on /start (SetupView wizard) and
 * /settings/profile.
 *
 * Returns a Promise that resolves with the popup's outcome. If the browser
 * blocks the popup, the Promise rejects with a `POPUP_BLOCKED` error so the
 * caller can show a toast.
 *
 * The callback route at /api/integrations/recal/callback detects the
 * `recal_display=popup` cookie set by the connect endpoint and responds with
 * an HTML page that postMessages this window and closes itself.
 */

export type RecalProvider = 'google' | 'microsoft';

export interface RecalOAuthResult {
  status: 'connected' | 'error';
  provider?: RecalProvider | string;
  error?: string;
}

export class RecalOAuthError extends Error {
  code: 'POPUP_BLOCKED' | 'POPUP_CLOSED' | 'ERROR_FROM_PROVIDER';
  detail?: string;

  constructor(code: 'POPUP_BLOCKED' | 'POPUP_CLOSED' | 'ERROR_FROM_PROVIDER', detail?: string) {
    super(detail || code);
    this.code = code;
    this.detail = detail;
  }
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 720;

export function openRecalOAuth(
  provider: RecalProvider,
  options?: { returnUrl?: string },
): Promise<RecalOAuthResult> {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams({ provider, display: 'popup' });
    if (options?.returnUrl) params.set('returnUrl', options.returnUrl);

    const url = `/api/integrations/recal/connect?${params.toString()}`;

    // Centered popup. Browsers may ignore these dimensions, that's fine.
    const left = Math.max(0, Math.floor((window.screen.availWidth - POPUP_WIDTH) / 2));
    const top = Math.max(0, Math.floor((window.screen.availHeight - POPUP_HEIGHT) / 2));
    const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`;

    const popup = window.open(url, 'apprendo-recal-oauth', features);
    if (!popup) {
      reject(new RecalOAuthError('POPUP_BLOCKED'));
      return;
    }

    let settled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'apprendo-recal') return;
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ status: data.status, provider: data.provider, error: data.error });
    };

    window.addEventListener('message', handleMessage);

    // Detect user closing the popup without completing OAuth. Poll once a
    // second; if `popup.closed` becomes true and we haven't received a
    // message, treat as cancellation.
    pollTimer = setInterval(() => {
      if (popup.closed && !settled) {
        settled = true;
        cleanup();
        reject(new RecalOAuthError('POPUP_CLOSED'));
      }
    }, 1000);
  });
}
